import { useState } from 'react';
import { Check, ChevronsUpDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';

// Reusable searchable dropdown (combobox) built on Command + Popover.
// options: [{ value, label, group? }]. `group` is an optional section heading.
export function SearchableSelect({
  options = [],
  value,
  onChange,
  placeholder = 'Select...',
  searchPlaceholder = 'Search...',
  emptyText = 'No results.',
  allowClear = true,
  testid,
}) {
  const [open, setOpen] = useState(false);
  const safeOptions = Array.isArray(options) ? options : [];
  const selectedLabel = safeOptions.find(o => o.value === value)?.label;

  // Preserve first-seen order of group headings.
  const groupOrder = [];
  safeOptions.forEach(o => {
    const g = o.group || '';
    if (!groupOrder.includes(g)) groupOrder.push(g);
  });

  const handleSelect = (val) => { onChange(val); setOpen(false); };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full justify-between bg-background border-border font-normal"
          data-testid={testid}
        >
          <span className={cn('truncate', !selectedLabel && 'text-muted-foreground')}>{selectedLabel || placeholder}</span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0 bg-card border-border" align="start">
        <Command>
          <CommandInput placeholder={searchPlaceholder} data-testid={testid ? `${testid}-input` : undefined} />
          <CommandList>
            <CommandEmpty>{emptyText}</CommandEmpty>
            {allowClear && value && (
              <CommandGroup>
                <CommandItem value="__clear_selection__" onSelect={() => handleSelect('')} className="text-muted-foreground text-sm">
                  Clear selection
                </CommandItem>
              </CommandGroup>
            )}
            {groupOrder.map(g => (
              <CommandGroup key={g || '__default'} heading={g || undefined}>
                {safeOptions.filter(o => (o.group || '') === g).map(o => (
                  <CommandItem
                    key={o.value}
                    value={`${o.label} ${o.value}`}
                    onSelect={() => handleSelect(o.value)}
                    className="text-sm"
                    data-testid={testid ? `${testid}-option-${o.value}` : undefined}
                  >
                    <Check className={cn('mr-2 h-4 w-4', value === o.value ? 'opacity-100' : 'opacity-0')} />
                    <span className="truncate">{o.label}</span>
                  </CommandItem>
                ))}
              </CommandGroup>
            ))}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

export default SearchableSelect;
